import { ConfigCatProvider, PollingMode } from 'configcat-react';
import { createContext, type ReactNode, useContext } from 'react';
import { usePaymentLinksFlag } from '@/hooks/usePaymentLinksFlag';

/**
 * An unset pipeline variable or manifest entry reaches the app as the literal macro `$(name)`,
 * which is not a usable key — treat it as absent so an unconfigured environment stays fail-closed.
 */
function normaliseSdkKey(value: string | undefined): string | undefined {
  const key = value?.trim();

  return key && !key.startsWith('$(') ? key : undefined;
}

const PaymentLinksEnabledContext = createContext(false);

/**
 * Whether the `paymentLinks` flag is on for the current tenant and user. It is `false` while the
 * ConfigCat config is loading and whenever no SDK key is configured, so routes, navigation and the
 * dashboard banner can read it unconditionally without a ConfigCat client being mounted.
 */
export function usePaymentLinksEnabled(): boolean {
  return useContext(PaymentLinksEnabledContext);
}

interface FeatureFlagProviderProps {
  /** Public client-side ConfigCat SDK key, supplied per environment by the host manifest. */
  sdkKey?: string;
  children: ReactNode;
}

/**
 * Mounts the ConfigCat client. Flag evaluation is asynchronous and never blocks the tree —
 * children render immediately and each flag resolves to its default until the config arrives.
 */
export function FeatureFlagProvider({ sdkKey, children }: Readonly<FeatureFlagProviderProps>): React.ReactElement {
  const key = normaliseSdkKey(sdkKey);

  if (!key) {
    return <PaymentLinksEnabledContext.Provider value={false}>{children}</PaymentLinksEnabledContext.Provider>;
  }

  return (
    <ConfigCatProvider sdkKey={key} pollingMode={PollingMode.AutoPoll} options={{ pollIntervalSeconds: 300 }}>
      <PaymentLinksFlagGate>{children}</PaymentLinksFlagGate>
    </ConfigCatProvider>
  );
}

function PaymentLinksFlagGate({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
  const { isEnabled } = usePaymentLinksFlag();

  return <PaymentLinksEnabledContext.Provider value={isEnabled}>{children}</PaymentLinksEnabledContext.Provider>;
}
